import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BTN_PAGE_PRIMARY_ACTION } from "@/lib/buttonStyles";
import { deviceHasCamera } from "@/lib/deviceCamera";
import { api, type AiStockAnalyzedItem } from "@/lib/api";
import { ModalAiStockCapture } from "@/components/estoque/ModalAiStockCapture";
import { ModalAiStockReview } from "@/components/estoque/ModalAiStockReview";
import { AiStockCameraCapture } from "@/components/estoque/AiStockCameraCapture";

type Props = {
  enabled: boolean;
  onSuccess: () => void;
};

/**
 * Botão + fluxo Adicionar Entrada (câmera mobile ou modal desktop + revisão).
 */
export function AiStockEntradaFlow({ enabled, onSuccess }: Props) {
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [itens, setItens] = useState<AiStockAnalyzedItem[]>([]);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [starting, setStarting] = useState(false);

  const openReview = useCallback((next: AiStockAnalyzedItem[], sid: number | null) => {
    setItens(next);
    setSessionId(sid);
    setDesktopOpen(false);
    setMobileOpen(false);
    setReviewOpen(true);
  }, []);

  const closeReview = useCallback(() => {
    const sid = sessionId;
    setReviewOpen(false);
    setItens([]);
    setSessionId(null);
    if (sid != null) {
      void api.aiStockCancelSession(sid).catch(() => undefined);
    }
  }, [sessionId]);

  const handleClick = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const hasCam = await deviceHasCamera();
      if (hasCam) {
        setMobileOpen(true);
      } else {
        setDesktopOpen(true);
      }
    } finally {
      setStarting(false);
    }
  };

  if (!enabled) return null;

  return (
    <>
      <Button
        type="button"
        className={BTN_PAGE_PRIMARY_ACTION}
        disabled={starting}
        onClick={() => void handleClick()}
      >
        <Plus className="h-4 w-4 shrink-0" />
        Adicionar Entrada
      </Button>

      <ModalAiStockCapture
        open={desktopOpen}
        onClose={() => setDesktopOpen(false)}
        onResult={(rows, sid) => openReview(rows, sid)}
      />

      {mobileOpen && typeof document !== "undefined"
        ? createPortal(
            <div className="fixed inset-0 z-[120] overflow-y-auto bg-white">
              <AiStockCameraCapture
                mode="in-app"
                onBack={() => setMobileOpen(false)}
                onResult={(rows) => {
                  if (rows.length === 0) {
                    setMobileOpen(false);
                    return;
                  }
                  openReview(rows, null);
                }}
              />
            </div>,
            document.body,
          )
        : null}

      <ModalAiStockReview
        open={reviewOpen}
        itens={itens}
        sessionId={sessionId}
        onClose={closeReview}
        onSuccess={() => {
          setReviewOpen(false);
          setItens([]);
          setSessionId(null);
          onSuccess();
        }}
      />
    </>
  );
}
